/**
 * MobileMenu — the global mobile nav drawer (replaces the per-page hamburger
 * that used to live only on the dashboard). Same NAV_TREE + gates as desktop,
 * rendered as accordions. Top: search (opens the ⌘K palette). Then "Jump back
 * in" recents, domain accordions with roll-up dots, and a footer carrying the
 * SAME items as the desktop user dropdown — My Schedule, Customize Dashboard,
 * Flerpie, Admin, Settings, Log out. Keep the two in step: there is no user
 * dropdown on mobile, so anything only in NavBar's UserDropdown is unreachable
 * on a phone. Backdrop + slide pattern mirrors SlideMenu.
 */
import React, { useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import useUserStore from '../stores/useUserStore';
import useNavPrefsStore from '../stores/useNavPrefsStore';
import useDashboardConfigStore from '../stores/useDashboardConfigStore';
import { instance, isSwarm } from '../instance';
import { getVisibleTree, resolveDestination, NavDomain } from '../navigation/navTree';
import { routeAllowedOnInstance } from '../navigation/routeInstances';
import { NavAlerts } from './useNavAlerts';
import { Icon } from './navIcons';
import { NavDot } from './NavDot';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  alerts: NavAlerts;
  onOpenPalette: () => void;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, user, alerts, onOpenPalette }) => {
  const tree = useMemo(() => getVisibleTree(user), [user]);
  const logout = useUserStore((s) => s.logout);
  const flerpieHidden = useNavPrefsStore((s) => s.flerpieHidden);
  const setFlerpieHidden = useNavPrefsStore((s) => s.setFlerpieHidden);
  const recents = useNavPrefsStore((s) => s.recents);
  const setEditMode = useDashboardConfigStore((s) => s.setEditMode);
  const history = useHistory();
  const [openKey, setOpenKey] = useState<string | null>(null);

  // Mirrors UserDropdown in NavBar.tsx.
  const canTravel =
    routeAllowedOnInstance('/my-schedule') &&
    (user?.isadmin || user?.can_admin || user?.can_operations || user?.can_producer ||
     user?.can_pilot || user?.travel_tier);
  const isAdmin = user?.isadmin || user?.can_admin;
  const handleCustomize = () => {
    onClose();
    if (window.location.pathname !== '/') history.push('/');
    setTimeout(() => setEditMode(true), 100);
  };

  // No Flerpie on Swarm (decision D4).
  const canSeeFlerpie = !isSwarm && (user?.can_ai || user?.isadmin || user?.can_admin);
  const flerpieOn = !flerpieHidden;

  const domainCount = (d: NavDomain) =>
    d.columns.reduce(
      (sum, c) => sum + c.items.reduce((a, i) => a + (i.notif && alerts[i.notif] ? alerts[i.notif] : 0), 0),
      0,
    );

  // Resolve recents to nav destinations, dedupe on the resolved route (visiting
  // /customers/1 then /customers/2 both collapse to /customers → one chip).
  const recentDests = useMemo(() => {
    const seen = new Set<string>();
    const out: { label: string; to: string }[] = [];
    for (const p of recents) {
      const d = resolveDestination(p, user);
      if (d && !seen.has(d.to)) {
        seen.add(d.to);
        out.push(d);
      }
      if (out.length >= 3) break;
    }
    return out;
  }, [recents, user]);

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        {...((!isOpen ? { inert: '', 'aria-hidden': true } : {}) as any)}
        className={`fixed top-0 right-0 z-50 bg-[#151515]/95 backdrop-blur-md border-l border-gray-800 shadow-[-4px_0_24px_rgba(0,0,0,0.5)] transition-transform duration-300 ease-out flex flex-col h-screen [height:100dvh] w-80 max-w-[88vw] ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="navigation"
      >
        <div className="flex shrink-0 items-center justify-between p-4 border-b border-gray-800">
          <span className="text-on-surface-variant text-xs uppercase tracking-wider">Menu</span>
          <button
            onClick={onClose}
            aria-label="Close menu"
            className="p-2 rounded text-on-surface-variant hover:text-white hover:bg-gray-800 transition-colors"
          >
            <Icon name="close" strokeWidth={2} className="w-4 h-4" />
          </button>
        </div>

        <button
          onClick={() => {
            onClose();
            onOpenPalette();
          }}
          className="mx-4 mt-3 flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-[#2a2a2a] border border-gray-700/60 text-on-surface-variant text-[13px]"
        >
          <Icon name="search" strokeWidth={1.6} className="w-4 h-4" />
          Search {instance.displayName}…
        </button>

        {recentDests.length > 0 && (
          <div className="px-4 pt-3 pb-1">
            <div className="text-[9.5px] tracking-[0.16em] uppercase text-on-surface-variant font-semibold mb-2">Jump back in</div>
            <div className="flex flex-wrap gap-2">
              {recentDests.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  onClick={onClose}
                  className="text-[12px] text-on-surface bg-[#2a2a2a] border border-gray-700/60 rounded-full px-3 py-1.5 hover:text-white"
                >
                  {r.label}
                </Link>
              ))}
            </div>
          </div>
        )}

        <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 py-2">
          {tree.map((d) => {
            const count = domainCount(d);
            const isOpenD = openKey === d.key;
            return (
              <div key={d.key} className="border-b border-gray-800/70">
                <button
                  onClick={() => setOpenKey(isOpenD ? null : d.key)}
                  className="w-full flex items-center gap-3 px-2 py-3.5 text-left"
                >
                  <Icon name={d.icon} className="w-[18px] h-[18px] text-on-surface-variant" />
                  <span className="text-[14.5px] font-medium text-white">{d.label}</span>
                  {count > 0 && <NavDot pulse className="ring-2 ring-[#151515]" />}
                  <Icon
                    name="chevron"
                    strokeWidth={2.2}
                    className={`ml-auto w-4 h-4 text-on-surface-variant transition-transform ${isOpenD ? 'rotate-180' : ''}`}
                  />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${isOpenD ? 'max-h-[640px]' : 'max-h-0'}`}>
                  {d.columns.map((col) =>
                    col.items.map((item) => {
                      const c = item.notif ? alerts[item.notif] || 0 : 0;
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={onClose}
                          className="flex items-center gap-2 pl-[42px] pr-3 py-3 text-[13.5px] text-on-surface-variant hover:text-white"
                        >
                          {item.label}
                          {c > 0 && <NavDot count={c} className="ml-1" />}
                        </Link>
                      );
                    }),
                  )}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="shrink-0 border-t border-gray-800 p-3 space-y-2">
          {/* One-tap path to the receipt camera; /receipts is ungated so no capability check. */}
          <Link
            to="/receipts?scan=1"
            onClick={onClose}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-primary/10 border border-primary/30 text-[13.5px] text-primary hover:bg-primary/15 transition-colors"
          >
            <Icon name="scan" strokeWidth={1.8} className="w-4 h-4" />
            Scan a receipt
          </Link>
          {canTravel && (
            <Link to="/my-schedule" onClick={onClose} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13.5px] text-on-surface hover:text-white text-left">
              <Icon name="sched" strokeWidth={1.8} className="w-4 h-4" />
              My Schedule
            </Link>
          )}
          <button onClick={handleCustomize} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13.5px] text-on-surface hover:text-white text-left">
            <Icon name="tools" strokeWidth={1.8} className="w-4 h-4" />
            Customize Dashboard
          </button>
          {isAdmin && (
            <Link to="/admin" onClick={onClose} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13.5px] text-on-surface hover:text-white text-left">
              <Icon name="tools" strokeWidth={1.8} className="w-4 h-4" />
              Admin
            </Link>
          )}
          {canSeeFlerpie && (
            <button
              onClick={() => setFlerpieHidden(!flerpieHidden)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-[13.5px] text-on-surface"
              aria-pressed={flerpieOn}
            >
              <span>Flerpie assistant</span>
              <span
                className={`relative w-[34px] h-[20px] rounded-full border transition-colors ${
                  flerpieOn ? 'bg-primary/20 border-primary' : 'bg-gray-700/40 border-outline-variant'
                }`}
              >
                <span
                  className={`absolute top-[2px] left-[2px] w-[14px] h-[14px] rounded-full transition-transform duration-200 ${
                    flerpieOn ? 'translate-x-[14px] bg-primary' : 'translate-x-0 bg-on-surface-variant'
                  }`}
                />
              </span>
            </button>
          )}
          <div className="flex gap-2">
            <Link
              to="/settings"
              onClick={onClose}
              className="flex-1 text-center py-2.5 rounded-lg bg-[#2a2a2a] border border-gray-700/60 text-[13px] text-on-surface hover:text-white"
            >
              Settings
            </Link>
            <button
              onClick={() => {
                logout();
                onClose();
              }}
              className="flex-1 py-2.5 rounded-lg bg-red-900/40 text-red-400 text-[13px] border border-red-800/60 hover:bg-red-900/60"
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
